import { dataDomains, type DataDomain } from '@/queries/dataDomains'
import { queryClient } from '@/queries/queryClient'
import { useQuery } from '@tanstack/vue-query'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

export const appLoadError = ref<Error | null>(null)

let lastRequired: readonly DataDomain[] = []

export const ensureRouteData = async (required: readonly DataDomain[]): Promise<void> => {
  lastRequired = required
  try {
    await Promise.all(
      required.map((domain) =>
        queryClient.ensureQueryData(dataDomains[domain]() as ReturnType<(typeof dataDomains)['dogs']>),
      ),
    )
    appLoadError.value = null
  } catch (error) {
    appLoadError.value = error instanceof Error ? error : new Error('Application load failed. Please contact technical team for support')
  }
}

export const retryRouteLoad = (): Promise<void> => ensureRouteData(lastRequired)

/**
 * Fixes R2-5006: editing a dog's profile could crash with `Data domain "orders" was not
 * preloaded for route "dogs-edit-profile"`.
 *
 * `ensureRouteData` above preloads each required domain with `ensureQueryData` — a one-off
 * fetch that does NOT create a `useQuery` observer. If nothing else happens to read that
 * domain for a while (e.g. it's only used on the last step of a multi-step wizard), it sits
 * with zero observers, and TanStack Query's `gcTime` (5 min default) evicts it from cache.
 * By the time the component that needs it finally mounts, the data is gone and it throws.
 *
 * This keeps one observer alive per domain for as long as the current route requires it, so
 * it can never drop to zero observers — and therefore can never become eligible for GC —
 * while the route needs it. Call once from the app root.
 *
 * (`dataDomains` is read inside the function, not at module scope, to dodge a circular
 * import — dataDomains -> a query file -> useClient -> authenticatedFetch -> router ->
 * appLoad -> dataDomains — that would otherwise make it `undefined` here.)
 */
export const useKeepRouteDataAlive = (): void => {
  const route = useRoute()
  const domains = Object.keys(dataDomains) as DataDomain[]

  domains.forEach((domain) => {
    const options = dataDomains[domain]() as ReturnType<(typeof dataDomains)['dogs']>

    useQuery({
      ...options,
      enabled: computed(() => (route.meta.requires ?? []).includes(domain)),
    })
  })
}
